import { useEffect, useRef, useState } from 'react';
import {
    peakAmplitude,
    amplitudeToDbfs,
    levelToSegments,
    nextSilenceTracker,
    silenceDurationMs,
    EMPTY_SILENCE_TRACKER,
    PRE_RECORDING_WARNING_MS,
    RECORDING_WARNING_MS
} from './micMeterLogic';

// Fast enough to feel live, slow enough to stay cheap.
const SAMPLE_INTERVAL_MS = 120;
const FFT_SIZE = 2048;

// Stable identity so the null-stream path cannot force a pointless render.
const HIDDEN = Object.freeze({ segments: 0, silenceMs: 0, available: false });

// Any of these means the document has user activation, which is what an
// AudioContext needs before it will leave the suspended state. pointerdown alone
// would strand keyboard-only users with a permanently hidden meter.
const GESTURE_EVENTS = ['pointerdown', 'keydown'];

/**
 * Live microphone input level for the meter on the recording screen.
 *
 * Owns its own AudioContext rather than sharing the one startRecording builds
 * for mixing: that graph only exists while recording, and a cosmetic meter must
 * never be able to break a capture.
 *
 * @param {MediaStream|null} microphoneStream
 * @returns {{segments: number, silenceMs: number, available: boolean}}
 */
export default function useMicLevel(microphoneStream) {
    const [meter, setMeter] = useState(HIDDEN);
    const audioContextRef = useRef(null);

    // One AudioContext for the hook's lifetime. Creating one per stream change
    // would accumulate against Chrome's per-page cap across stop/restart cycles.
    useEffect(() => {
        return () => {
            const ctx = audioContextRef.current;
            audioContextRef.current = null;
            if (ctx && ctx.state !== 'closed') {
                ctx.close().catch(() => {});
            }
        };
    }, []);

    useEffect(() => {
        if (!microphoneStream) {
            setMeter(HIDDEN);
            return undefined;
        }

        let cancelled = false;
        let intervalId = null;
        let sourceNode = null;
        let clonedStream = null;
        let removeGestureListeners = null;
        let watchedCtx = null;

        // Only re-render when something the UI actually shows has changed. The
        // silence figure matters only as it crosses a warning threshold, so
        // returning the previous object lets React bail out of the render.
        const publish = (segments, silenceMs, available) => {
            setMeter((previous) => {
                const crossed = [PRE_RECORDING_WARNING_MS, RECORDING_WARNING_MS].some(
                    (t) => (previous.silenceMs >= t) !== (silenceMs >= t)
                );
                if (previous.segments === segments && previous.available === available && !crossed) {
                    return previous;
                }
                return { segments, silenceMs, available };
            });
        };

        // Stays armed across repeated gestures. resume() only takes effect once the
        // document has user activation, and it does not flip ctx.state
        // synchronously inside the handler, so a listener that disarmed itself
        // after the first gesture would burn that gesture and never retry.
        const waitForGesture = (onGesture) => {
            if (removeGestureListeners) return;
            const handler = () => { if (!cancelled) onGesture(); };
            GESTURE_EVENTS.forEach((evt) => document.addEventListener(evt, handler));
            removeGestureListeners = () => {
                GESTURE_EVENTS.forEach((evt) => document.removeEventListener(evt, handler));
                removeGestureListeners = null;
            };
        };

        const start = () => {
            // Already sampling: a queued gesture or statechange must not attach a
            // second analyser, clone and interval.
            if (cancelled || intervalId) return;

            let ctx = audioContextRef.current;
            if (!ctx || ctx.state === 'closed') {
                try {
                    ctx = new (window.AudioContext || window.webkitAudioContext)();
                    audioContextRef.current = ctx;
                } catch (err) {
                    console.warn('Mic meter: could not create AudioContext', err);
                    return;
                }
            }

            // Chrome creates contexts suspended until the page has seen a user
            // gesture. Drive the retry off statechange rather than off the gesture
            // itself: resume() does not flip ctx.state synchronously inside the
            // handler, so starting from the gesture would always find it still
            // suspended and give up. Symptom was the meter staying hidden for a
            // whole recording when the only click was Start Meeting.
            if (ctx.state === 'suspended') {
                setMeter(HIDDEN);
                watchedCtx = ctx;
                ctx.onstatechange = () => {
                    if (ctx.state === 'running') {
                        ctx.onstatechange = null;
                        watchedCtx = null;
                        start();
                    }
                };
                waitForGesture(() => ctx.resume().catch(() => {}));
                ctx.resume().catch(() => {});
                return;
            }

            try {
                // Analyse a clone: two MediaStreamSource nodes built from the same
                // stream on one context leave the second silent in Chrome (the same
                // workaround useQASession relies on).
                clonedStream = microphoneStream.clone();
                // The clone inherits `enabled` as it was at clone time and ignores
                // later toggles, so force it on to measure the physical input. The
                // app's own Mute state is reported separately via isMicrophoneActive.
                clonedStream.getAudioTracks().forEach((track) => { track.enabled = true; });

                const analyser = ctx.createAnalyser();
                analyser.fftSize = FFT_SIZE;

                sourceNode = ctx.createMediaStreamSource(clonedStream);
                sourceNode.connect(analyser);

                const samples = new Float32Array(analyser.fftSize);
                let tracker = EMPTY_SILENCE_TRACKER;

                intervalId = setInterval(() => {
                    try {
                        analyser.getFloatTimeDomainData(samples);
                        const amplitude = peakAmplitude(samples);
                        // performance.now() is monotonic; a wall-clock jump backwards
                        // would otherwise stall an in-progress silence timer.
                        const now = performance.now();

                        tracker = nextSilenceTracker(tracker, amplitude, now);
                        publish(
                            levelToSegments(amplitudeToDbfs(amplitude)),
                            silenceDurationMs(tracker, now),
                            true
                        );
                    } catch (err) {
                        console.warn('Mic meter: sampling failed, stopping', err);
                        clearInterval(intervalId);
                        intervalId = null;
                        setMeter(HIDDEN);
                    }
                }, SAMPLE_INTERVAL_MS);

                // Attached: the gesture listener has done its job.
                if (removeGestureListeners) removeGestureListeners();
                publish(0, 0, true);
            } catch (err) {
                console.warn('Mic meter: could not attach analyser', err);
                setMeter(HIDDEN);
            }
        };

        start();

        return () => {
            cancelled = true;
            if (removeGestureListeners) removeGestureListeners();
            if (watchedCtx) watchedCtx.onstatechange = null;
            if (intervalId) clearInterval(intervalId);
            if (sourceNode) {
                try { sourceNode.disconnect(); } catch { /* already torn down */ }
            }
            // Release the clone, or the tab keeps showing a mic-in-use indicator.
            if (clonedStream) {
                clonedStream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [microphoneStream]);

    return meter;
}
